import React, { useState } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { formatApiError, portalApi } from '../../services/api';
import { Car, EnvelopeSimple } from '@phosphor-icons/react';

export default function PortalLogin() {
  const { customer, login } = usePortalAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');

  if (customer) return <Navigate to="/portal" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success('Willkommen zurück!');
      navigate('/portal');
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await portalApi.requestMagicLink(forgotEmail);
      toast.success('Falls ein Konto existiert, haben wir einen Link versandt.');
      setForgotMode(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F7F8FA] to-[#E9EBF3] p-4" data-testid="portal-login-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 justify-center mb-6">
          <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center">
            <Car weight="fill" size={28} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-[#111418]">Kunden-Portal</h1>
            <p className="text-sm text-[#8A94A6]">Ihr Vermieter-Konto</p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-[#E2E4E9] p-6">
          {!forgotMode ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="portal-login-form">
              <div>
                <Label className="overline">E-Mail</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus autoComplete="email" data-testid="portal-login-email" className="input-swiss" />
              </div>
              <div>
                <Label className="overline">Passwort</Label>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required autoComplete="current-password" data-testid="portal-login-password" className="input-swiss" />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded" disabled={busy} data-testid="portal-login-submit">
                {busy ? 'Bitte warten…' : 'Anmelden'}
              </Button>
              <button type="button" onClick={() => { setForgotMode(true); setForgotEmail(email); }} className="text-sm text-indigo-600 hover:underline w-full text-center" data-testid="portal-forgot-btn">
                Passwort vergessen? Zugangs-Link anfordern
              </button>
            </form>
          ) : (
            <form onSubmit={handleForgot} className="space-y-4" data-testid="portal-forgot-form">
              <div className="text-center mb-2">
                <EnvelopeSimple weight="thin" size={40} className="mx-auto text-indigo-600 mb-2" />
                <h2 className="text-lg font-semibold text-[#111418]">Zugangs-Link anfordern</h2>
                <p className="text-sm text-[#525866] mt-1">Wir senden Ihnen einen Link zur Passwortvergabe.</p>
              </div>
              <div>
                <Label className="overline">E-Mail</Label>
                <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} required data-testid="portal-forgot-email" className="input-swiss" />
              </div>
              <Button type="submit" className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2.5 rounded" disabled={busy} data-testid="portal-forgot-submit">
                Link anfordern
              </Button>
              <button type="button" onClick={() => setForgotMode(false)} className="text-sm text-[#525866] hover:text-[#111418] w-full text-center">
                ← Zurück zum Login
              </button>
            </form>
          )}
        </div>

        <p className="text-center text-xs text-[#8A94A6] mt-6">
          Sie haben noch keinen Zugang? Bitte wenden Sie sich an Ihren Vermieter.
        </p>
      </div>
    </div>
  );
}
